import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CustomTabs from '../../../CustomTabs/CustomTabs';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';
import BitlyShortlinkSettingsSection from './BitlyShortlinkSettingsSection';

const BasicSettings = () => {
  /**
   * 1) Access submissionSettings & updateSettings from context.
   *    We'll specifically sync with the "displayInCard" action.
   */
  const { submissionSettings, updateSettings } = useFormSubmissionSettings();

  // 2) If user hasn't selected "displayInCard" yet, provide defaults (e.g. from getDefaultSubmitButtonConfig).
  //    We'll assume your new FormSubmissionSettingsContext sets it to { enabled: false } if not used.
  const {
    cardTitle: contextCardTitle = 'Submission Successful',
    cardDescription: contextCardDescription = 'Thank you for your submission.',
    errorMessage: contextErrorMessage, // No fallback here; it comes from the context's default
    enableBitly: contextEnableBitly = false,
    clearFormOnSubmit: contextClearFormOnSubmit = false,
    saveAsDraft: contextSaveAsDraft = false,
    copyIconEnabled: contextCopyIconEnabled = false,
  } = submissionSettings?.displayInCard || {};

  // 3) Local state for UI input fields, initialized from context:
  //    We only update the context after user "clicks away" (blur event).
  const [cardTitle, setCardTitle] = useState(contextCardTitle);
  const [cardDescription, setCardDescription] = useState(contextCardDescription);
  const [errorMessage, setErrorMessage] = useState(contextErrorMessage);
  const [enableBitly, setEnableBitly] = useState(contextEnableBitly);
  const [clearFormOnSubmit, setClearFormOnSubmit] = useState(contextClearFormOnSubmit);
  const [saveAsDraft, setSaveAsDraft] = useState(contextSaveAsDraft);
  const [copyIconEnabled, setCopyIconEnabled] = useState(contextCopyIconEnabled);

  // 4) useEffect to sync local state with context changes.
  //    If the user changes "displayInCard" somewhere else, we'll re-sync here.
  useEffect(() => {
    setCardTitle(contextCardTitle);
    setCardDescription(contextCardDescription);
    setErrorMessage(contextErrorMessage);
    setEnableBitly(contextEnableBitly);
    setClearFormOnSubmit(contextClearFormOnSubmit);
    setSaveAsDraft(contextSaveAsDraft);
    setCopyIconEnabled(contextCopyIconEnabled);
  }, [
    contextCardTitle,
    contextCardDescription,
    contextErrorMessage,
    contextEnableBitly,
    contextCopyIconEnabled,
    contextClearFormOnSubmit,
    contextSaveAsDraft,
  ]);

  /**
   * 5) Handlers for input changes:
   *    - Only update local state on keystroke
   *    - Call updateSettings('displayInCard', ...) on blur instead
   */

  // Title: local onChange
  const handleTitleChange = useCallback((e) => {
    const newValue = e.target.value;
    // console.log('[BasicSettings] handleTitleChange => newValue:', newValue);
    setCardTitle(newValue); // No context update yet
  }, []);

  // Title: update context on blur
  const handleTitleBlur = useCallback(() => {
    // console.log('[BasicSettings] handleTitleBlur => updating context with cardTitle:', cardTitle);
    updateSettings('displayInCard', {
      cardTitle,
    });
  }, [cardTitle, updateSettings]);

  // Description: local onChange
  const handleDescriptionChange = useCallback((e) => {
    const newValue = e.target.value;
    // console.log('[BasicSettings] handleDescriptionChange => newValue:', newValue);
    setCardDescription(newValue); // No context update yet
  }, []);

  // Description: update context on blur
  const handleDescriptionBlur = useCallback(() => {
    // console.log('[BasicSettings] handleDescriptionBlur => updating context with cardDescription:', cardDescription);
    updateSettings('displayInCard', {
      cardDescription,
    });
  }, [cardDescription, updateSettings]);

  // Error Message: local onChange
  const handleErrorMessageChange = useCallback((e) => {
    const newValue = e.target.value;
    // console.log('[BasicSettings] handleErrorMessageChange => newValue:', newValue);
    setErrorMessage(newValue); // Update local state
  }, []);

  // Error Message: update context on blur
  const handleErrorMessageBlur = useCallback(() => {
    // console.log('[BasicSettings] handleErrorMessageBlur => updating context with errorMessage:', errorMessage);
    updateSettings('displayInCard', {
      errorMessage,
    });
  }, [errorMessage, updateSettings]);


 /**
   * 6) Toggle handlers
   */
  const handleToggleCopyIcon = useCallback((e) => {
    const newValue = e.target.checked;
    setCopyIconEnabled(newValue);
    updateSettings('displayInCard', { copyIconEnabled: newValue });
  }, [updateSettings]);

  const handleToggleClearFormOnSubmit = useCallback((e) => {
    const newValue = e.target.checked;
    setClearFormOnSubmit(newValue);
    updateSettings('displayInCard', { clearFormOnSubmit: newValue });
  }, [updateSettings]);

  const handleToggleSaveAsDraft = useCallback((e) => {
    const newValue = e.target.checked;
    setSaveAsDraft(newValue);
    updateSettings('displayInCard', { saveAsDraft: newValue });
  }, [updateSettings]);

  /**
   * 7) Render Basic Settings UI
   */
  return (
    <Box sx={{ mt: 3 }}>
      <TextField
        variant="outlined"
        fullWidth
        value={cardTitle}
        onChange={handleTitleChange}
        onBlur={handleTitleBlur}
        label="Card Title"
        sx={{ mb: 2 }}
      />

      <TextField
        variant="outlined"
        fullWidth
        multiline
        minRows={3}
        value={cardDescription}
        onChange={handleDescriptionChange}
        onBlur={handleDescriptionBlur}
        label="Card Description"
        sx={{ mb: 2 }}
      />

      <TextField
        variant="outlined"
        fullWidth
        multiline
        minRows={2}
        value={errorMessage}
        onChange={handleErrorMessageChange}
        onBlur={handleErrorMessageBlur}
        label="Error Message"
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={<Switch checked={copyIconEnabled} onChange={handleToggleCopyIcon} size="small" />}
        label={<Typography variant="body2">Show Copy</Typography>}
        sx={{ display: 'flex', alignItems: 'center', ml: 0, mb: 2 }}
      />
    </Box>
  );
};

const AdvancedSettings = () => {
  /**
   * Display Card shortlink settings.
   *
   * Enterprise note:
   * - The UI stays in the Display Card Advanced tab because this is where
   *   builders naturally configure generated-output behavior.
   * - The organization Bitly connection is still managed centrally from
   *   /integrations.
   * - This phase keeps displayInCard.enableBitly as the UI/runtime mirror.
   * - The next phase will add publish-safe form-level integration config.
   */
  return (
    <Box sx={{ mt: 3 }}>
      <BitlyShortlinkSettingsSection
        actionType="displayInCard"
        generatedOutputLabel="Display Card generated URL"
      />
    </Box>
  );
};

const DisplayCardSubmitSettings = () => {
  const [activeTab, setActiveTab] = useState('basic');

  const handleChangeTab = useCallback((event, newValue) => {
    setActiveTab(newValue);
    console.log(`[DisplayCardSubmitSettings] Switched to tab: ${newValue}`);
  }, []);

  return (
    <Box>
      <CustomTabs value={activeTab} onChange={handleChangeTab} />
      {activeTab === 'basic' && <BasicSettings />}
      {activeTab === 'advanced' && <AdvancedSettings />}
    </Box>
  );
};

export default DisplayCardSubmitSettings;
